import { useEffect, useRef } from "react";

export default function VectorField({ className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w, h, dpr, raf;
    const mouse = { x: -9999, y: -9999 };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const N = 130;
    const pts = Array.from({ length: N }, () => ({
      x: Math.random(),
      y: Math.random(),
      a: Math.random() * Math.PI * 2,
      s: 0.25 + Math.random() * 0.6,
      l: 10 + Math.random() * 24,
    }));

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseout", onLeave);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of pts) {
        p.a += 0.0022;
        p.x = (p.x + Math.cos(p.a) * p.s * 0.0016 + 1) % 1;
        p.y = (p.y + Math.sin(p.a) * p.s * 0.0016 + 1) % 1;
        const px = p.x * w;
        const py = p.y * h;
        const dx = px - mouse.x;
        const dy = py - mouse.y;
        const d = Math.hypot(dx, dy);
        const near = d < 170 ? 1 - d / 170 : 0;
        const ang = near > 0 ? Math.atan2(dy, dx) : p.a;
        const alpha = 0.16 + near * 0.5;
        const len = p.l * (1 + near * 0.9);
        const x2 = px + Math.cos(ang) * len;
        const y2 = py + Math.sin(ang) * len;

        ctx.strokeStyle = near > 0.05 ? `rgba(14,124,196,${alpha})` : `rgba(11,74,143,${alpha * 0.6})`;
        ctx.lineWidth = 1 + near * 0.8;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(x2, y2);
        ctx.stroke();

        const hx = ang + Math.PI / 7;
        const hl = len * 0.28;
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - Math.cos(hx) * hl, y2 - Math.sin(hx) * hl);
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - Math.cos(ang - Math.PI / 7) * hl, y2 - Math.sin(ang - Math.PI / 7) * hl);
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return <canvas ref={ref} className={className} data-testid="hero-vector-field" aria-hidden="true" />;
}
